import { Component, OnInit, Input, Output, EventEmitter, NgZone, CUSTOM_ELEMENTS_SCHEMA } from '@angular/core';
import { Position, BindDeviceResult } from 'src/app/model';
import { IonicModule, ModalController, Platform } from '@ionic/angular';
import {
  ApiService,
  HelperProvider,
  UserProfile
} from 'src/app/providers';
import { generateCustomId } from 'src/app/app.utils';
import { DmStoreService } from 'src/app/store/dm-store.service';
import { DeviceBindStoreService } from 'src/app/store/device-bind-store.service';
import { LocaLstorageService } from 'src/app/store/localstorage.service';
import { GroupBindStoreService } from 'src/app/store/group-bind-store.service';
import { CommonModule } from '@angular/common';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { DeviceInputComponent } from '../device-input/device-input.component';
import { LabelsSelectComponent } from '../labels-select/labels-select.component';
import { GroupSelectComponent } from '../group-select/group-select.component';
import { ZoneSelectComponent } from '../zone-select/zone-select.component';
import { LevelSelectComponent } from '../level-select/level-select.component';
import { ProjectSelectComponent } from '../project-select/project-select.component';
import { MapsService } from 'src/app/providers/maps/maps.service';
import { OwnerSelectComponent } from '../owner-select/owner-select.component';

@Component({
  selector: 'app-device-bind',
  templateUrl: './device-bind.component.html',
  styleUrls: ['./device-bind.component.scss'],
  standalone: true,
  imports: [IonicModule, CommonModule, FormsModule, DeviceInputComponent, LabelsSelectComponent, GroupSelectComponent, ZoneSelectComponent, LevelSelectComponent, ProjectSelectComponent, ReactiveFormsModule, OwnerSelectComponent],
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
})
export class DeviceBindComponent implements OnInit {
  public isAdmin = false;
  public error: string;
  public deviceId: string;
  public customId: string;
  public networkId: string;
  private calibrated: boolean;
  public generatedCustomId: string;
  public deviceSkipped = false;

  private native = false;

  @Input() position: Position;

  @Output() updatedCustomId = new EventEmitter<string>();
  @Output() updatedNetworkId = new EventEmitter<string>();

  public customIdAutogen$ = this.localStorageService.customIdGeneration$;

  constructor(
    private dmStore: DmStoreService,
    private helper: HelperProvider,
    private api: ApiService,
    private modalController: ModalController,
    private ngZone: NgZone,
    private deviceBindStore: DeviceBindStoreService,
    private maps: MapsService,
    private localStorageService: LocaLstorageService,
    private groupBindStore: GroupBindStoreService,
    private platform: Platform,
    private userProfile: UserProfile,
  ) { }

  async ngOnInit() {
    this.isAdmin = await this.userProfile.isAdmin();
    this.native = this.platform.is('capacitor') && this.platform.is('android');

    if (this.position) {
      this.customId = this.position.custom_id || '';
      this.networkId = this.position.network_id || '';
    }

    if (this.localStorageService.customIdGeneration && !this.position) {
      this.generatedCustomId = generateCustomId();
    }
  }

  public newDevice = (device: string) => this.deviceId = device;
  public calibrationStatus = (status: boolean) => this.calibrated = status;
  public changeCustomId = () => this.updatedCustomId.emit(this.customId);
  public changeNetworkId = () => this.updatedNetworkId.emit(this.networkId);

  public async bindConfirm() {
    if (this.native && !this.calibrated && !this.deviceSkipped) {
      this.helper.confirm(this.bindAction.bind(this), 'Are you sure you want to bind uncalibrated device?');
    } else {
      this.bindAction();
    }
  }

  public async bindAction() {
    this.groupBindStore.openGroupBindTab = false;
    this.ngZone.run(() => this.error = '');

    if (this.deviceSkipped) {
      this.modalController.dismiss();
      this.deviceBindStore.deviceSkipped = this.deviceSkipped;
      this.deviceBindStore.device = null;
      this.deviceBindStore.group = this.dmStore.selectedGroup.getValue()?.id;
      this.deviceBindStore.zone = this.dmStore.selectedZone.getValue()?.id;
      this.deviceBindStore.accurateDevicePosition = true;
      this.deviceBindStore.newCustomId = this.generatedCustomId;
      if (this.deviceBindStore.group) {
        const { positions } = await this.api.positions.getPositions(
          { group_id: [this.deviceBindStore.group] },
          this.helper,
        );

        this.deviceBindStore.positionsInGroup = positions || [];

        if (positions.length) {
          this.maps.bound(positions.map(({ lat, lon }) => ({ lat, lon })))
        }
      }
      return;
    }

    this.helper.showLoading();

    const { device, error } = await this.api.devices.getDeviceById(this.deviceId);

    if (error) {
      this.ngZone.run(() => this.error = error || 'Unknown error');
      this.helper.hideLoading();
      return;
    }

    if (device?.damaged_status) {
      this.ngZone.run(() => this.error = 'Device is damaged');
      this.helper.hideLoading();
      return;
    }

    if (!this.position) {
      this.createPositionForDevice();
      return;
    }

    this.error = '';
    const result = await this.api.devices.bindDeviceToPosition(this.deviceId, { position_id: this.position.id });

    if (result.error) {
      this.ngZone.run(() => this.error = result.error || '');
      this.helper.hideLoading();
    } else {
      this.maps.bound([{ lat: this.position.lat, lon: this.position.lon }]);
      await this.dmStore.bindDevice(this.deviceId, this.position.id);
      const modalResult: BindDeviceResult = {
        binded: true,
      };
      this.modalController.dismiss(modalResult);
    }

    this.helper.hideLoading();
  }

  private async createPositionForDevice() {
    this.helper.showLoading();
    setTimeout(() => this.error = '');
    const { device, error } = await this.api.devices.getDeviceById(this.deviceId);
    if (error) {
      this.error = error;
      this.helper.hideLoading();
      return;
    }

    if (device && device.position_id) {
      this.error = 'Device is positioned already';
      this.helper.hideLoading();
      return;
    }

    const groupId = this.dmStore.selectedGroup.getValue()?.id;
    if (!groupId) {
      throw new Error('No group for position!');
    }

    const { positions } = await this.api.positions.getPositions({ group_id: [groupId] }, this.helper);

    this.deviceBindStore.positionsInGroup = positions || [];

    this.helper.hideLoading();

    if (!this.error) {
      this.deviceBindStore.deviceSkipped = this.deviceSkipped;
      this.deviceBindStore.device = device || null;
      this.deviceBindStore.group = this.dmStore.selectedGroup.getValue()?.id;
      this.deviceBindStore.zone = this.dmStore.selectedZone.getValue()?.id;
      this.deviceBindStore.accurateDevicePosition = true;
      this.deviceBindStore.newCustomId = this.generatedCustomId;
      this.modalController.dismiss();
    }
  }

  public skipSettingDevice() {
    this.deviceSkipped = !this.deviceSkipped;
  }

  public isSubmitDisabled = () => !this.dmStore.selectedGroup.getValue() || (!this.deviceSkipped ? !this.deviceId : false);
}
